// Revisions describe raw server messages, never the projected React state.
export const RAG_MESSAGE_DELTA_VERSION = 'v1';
const MAX_MESSAGES = 100;
const MAX_KNOWN_ENCODED_BYTES = 6000;
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isId = value => Number.isSafeInteger(value) && value > 0;
const isRevision = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);

export const ragMessagePageScope = ({ actorId, chatId, sessionId, generation, epoch, limit, beforeMessageId = 0 }) => (
    `${actorId}:${chatId}:${sessionId}:${generation}:${epoch}:${limit}:${beforeMessageId}`
);

export const addRagMessageDeltaParams = (params, cache, scope) => {
    if (cache?.scope === scope && cache.legacy) return;
    const pairs = [];
    if (cache?.scope === scope) {
        for (const message of cache.messages || []) {
            const revision = cache.revisions?.[message.id];
            if (!isId(message.id) || !isRevision(revision)) continue;
            const next = [...pairs, [message.id, revision]];
            if (next.length > MAX_MESSAGES || encodeURIComponent(JSON.stringify(next)).length > MAX_KNOWN_ENCODED_BYTES) break;
            pairs.push([message.id, revision]);
        }
    }
    params.set('messages_delta', RAG_MESSAGE_DELTA_VERSION);
    params.set('known_message_revisions', JSON.stringify(pairs));
};

const validMessages = (messages, chatId, limit) => {
    if (!Array.isArray(messages) || messages.length > limit || messages.length > MAX_MESSAGES) return false;
    const seen = new Set();
    return messages.every(message => {
        if (!isRecord(message) || !isId(message.id) || Number(message.chat_id) !== chatId || seen.has(message.id)) return false;
        seen.add(message.id);
        return true;
    });
};

export const resolveRagMessagePage = (data, cache, context) => {
    const { chatId, limit, beforeMessageId = 0, scope } = context;
    if (!isRecord(data) || !data.success || Number(data.chat?.id) !== chatId
        || !validMessages(data.messages, chatId, limit)) return null;
    if (data.message_delta === undefined) {
        // A legacy server already sent a complete page. Do not fetch it twice
        // or renegotiate until the chat/session cache scope changes.
        return { data, cache: { scope, legacy: true, messages: [], revisions: {} } };
    }
    const delta = data.message_delta;
    if (!isRecord(delta) || delta.version !== RAG_MESSAGE_DELTA_VERSION || delta.chat_id !== chatId
        || delta.page?.limit !== limit || delta.page?.before_message_id !== beforeMessageId
        || !Array.isArray(delta.ordered_message_ids) || delta.ordered_message_ids.length > limit
        || delta.ordered_message_ids.length > MAX_MESSAGES || !isRecord(delta.revisions)) return null;
    const ids = delta.ordered_message_ids;
    const idSet = new Set(ids);
    if (ids.some(id => !isId(id)) || idSet.size !== ids.length
        || Object.keys(delta.revisions).length !== ids.length
        || Object.keys(delta.revisions).some(id => !idSet.has(Number(id)) || String(Number(id)) !== id)
        || ids.some(id => !isRevision(delta.revisions[id]))) return null;
    if (!isRecord(data.message_pagination) || typeof data.message_pagination.has_more_before !== 'boolean'
        || data.message_pagination.oldest_message_id !== (ids[0] ?? null)) return null;
    const changed = new Map(data.messages.map(message => [message.id, message]));
    if (data.messages.some(message => !idSet.has(message.id))) return null;
    const previous = new Map(cache?.scope === scope ? (cache.messages || []).map(message => [message.id, message]) : []);
    const messages = [];
    for (const id of ids) {
        let message = changed.get(id);
        if (!message) {
            if (cache?.scope !== scope || cache.revisions?.[id] !== delta.revisions[id]) return null;
            message = previous.get(id);
            if (!message || Number(message.chat_id) !== chatId) return null;
        }
        messages.push(message);
    }
    return { data: { ...data, messages }, cache: { scope, messages, revisions: { ...delta.revisions }, legacy: false } };
};

export const fetchRagMessagePage = async ({ fetchPage, params, cache, context, isCurrent = () => true }) => {
    const requestParams = new URLSearchParams(params);
    addRagMessageDeltaParams(requestParams, cache, context.scope);
    let data = await fetchPage(requestParams);
    if (!isCurrent()) return null;
    // Authentication/server failures are not cache misses and never get retried.
    if (!data?.success) return { data, cache: null };
    let resolved = resolveRagMessagePage(data, cache, context);
    if (resolved) return resolved;
    // Exactly one complete-page retry. No timestamp/cache watermark is changed
    // unless this second response is fully valid and still belongs to the turn.
    const fullParams = new URLSearchParams(params);
    fullParams.delete('messages_delta');
    fullParams.delete('known_message_revisions');
    data = await fetchPage(fullParams);
    if (!isCurrent()) return null;
    if (!data?.success) return { data, cache: null };
    if (data.message_delta !== undefined) throw new Error('Invalid complete chat page');
    resolved = resolveRagMessagePage(data, null, context);
    if (!resolved) throw new Error('Incomplete chat page');
    return resolved;
};

export const createRagSilentReloadCoordinator = () => {
    const flights = new Map();
    return {
        run(key, load, isCurrent) {
            const existing = flights.get(key);
            if (existing) {
                existing.dirty = true;
                return existing.promise;
            }
            const flight = { dirty: false, promise: null };
            // Defer load until this flight is registered, including synchronously
            // resolving test/fetch adapters. Later triggers request a trailing
            // snapshot instead of being lost behind the earlier in-flight GET.
            flight.promise = Promise.resolve().then(async () => {
                do {
                    flight.dirty = false;
                    if (!isCurrent()) return;
                    await load();
                } while (flight.dirty && isCurrent());
            }).finally(() => {
                if (flights.get(key) === flight) flights.delete(key);
            });
            flights.set(key, flight);
            return flight.promise;
        },
    };
};
